import { useCallback, useEffect, useMemo, useState } from 'react';
import DashboardPageFrame from '../components/DashboardPageFrame.jsx';
import MasterCrudTable from '../components/MasterCrudTable.jsx';
import TemplateSelectionPopup from '../components/TemplateSelectionPopup.jsx';
import {
  MASTER_DEFINITIONS,
  MASTER_GROUPS,
  USER_DEFINITIONS,
  LIFE_DEFINITIONS,
  LIFE_GROUPS,
  getMasterDefinition,
} from '../constants/masterDefinitions.js';
import { generateFromTemplate, getMasterItems, triggerNotionSync } from '../api/dashboardApi.js';
import { canAdmin, canSuperAdmin, canSystemAdmin } from '../../../auth/permissions.js';
import '../styles/dashboard-master.css';

const SUPER_ADMIN_KEYS = new Set(['functions', 'permissions']);

const USER_GROUP = MASTER_GROUPS.find((g) => g.colorClass === 'user');

/**
 * Notion同期カードを表示するか。
 * 検証(dev)・商用(prd)では Notion 取得を画面から行わない運用に変更したため、
 * 明示的に有効化したローカル開発環境でのみ表示する。未設定なら非表示。
 */
function isNotionSyncEnabled() {
  return process.env.REACT_APP_NOTION_SYNC_ENABLED === 'true';
}

/** ヘッダーに表示する機能リンク（各機能のホーム画面へ遷移） */
const HEADER_TABS = [
  { label: 'Dashboard', href: '/dashboard' },
  { label: 'Life', href: '/life' },
];

/** 機能選択ボックスの選択肢 */
function getFunctionOptions() {
  const options = [
    { value: 'ダッシュボード', label: 'ダッシュボード' },
    { value: 'Life', label: 'Life' },
    { value: 'AWS利用料', label: 'AWS利用料' },
  ];
  if (canSystemAdmin()) {
    options.push({ value: 'システム管理', label: 'システム管理' });
  }
  return options;
}

/** システム管理: 各機能ごとのセクション定義 */
const SYSTEM_ADMIN_SECTIONS = [
  { function: 'ダッシュボード', groups: MASTER_GROUPS },
  { function: 'Life', label: 'Life', groups: [...LIFE_GROUPS, .../* istanbul ignore next -- MASTER_GROUPS に user グループは必ずある */ (USER_GROUP ? [USER_GROUP] : [])] },
  { function: 'AWS利用料', groups: /* istanbul ignore next -- 同上 */ (USER_GROUP ? [USER_GROUP] : []) },
  { function: '全機能', groups: /* istanbul ignore next -- 同上 */ (USER_GROUP ? [USER_GROUP] : []) },
];

export default function DashboardMasterPage() {
  const [allData, setAllData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // 機能切り替え
  const [activeFunction, setActiveFunction] = useState('ダッシュボード');

  // Active table popup — popupFunction tracks which function's context to use
  const [activeKey, setActiveKey] = useState(null);
  const [popupFunction, setPopupFunction] = useState(null);

  // Schedule template generation state
  const [selectedOemId, setSelectedOemId] = useState('');
  const [selectedRegionId, setSelectedRegionId] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generateResult, setGenerateResult] = useState(null);
  const [templatePopupOpen, setTemplatePopupOpen] = useState(false);

  // Notion同期 state
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const loadAllData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const allDefs = [...MASTER_DEFINITIONS, ...USER_DEFINITIONS, ...LIFE_DEFINITIONS];
      const extraEndpoints = ['functions', 'permissions'];
      const results = await Promise.all([
        ...allDefs.map((def) => getMasterItems(def.endpoint)),
        ...extraEndpoints.map((ep) => getMasterItems(ep)),
      ]);
      const data = {};
      allDefs.forEach((def, i) => {
        data[def.key] = results[i];
      });
      extraEndpoints.forEach((ep, i) => {
        data[ep] = results[allDefs.length + i];
      });
      setAllData(data);
    } catch (loadError) {
      setError(loadError.message || 'マスタデータの取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  const isSystemAdmin = activeFunction === 'システム管理';
  const isDashboard = activeFunction === 'ダッシュボード';
  const isSuperAdmin = canSuperAdmin(activeFunction);
  const maxAssignableId = canSystemAdmin() ? 5 : (isSuperAdmin ? 3 : 2);
  const assignablePermissions = useMemo(
    () => (allData.permissions || []).filter((p) => p.id <= maxAssignableId),
    [allData.permissions, maxAssignableId],
  );

  // ポップアップ表示時の機能コンテキスト
  const popupFunctionName = popupFunction || activeFunction;

  const functionRecord = useMemo(
    () => (allData.functions || []).find((f) => f.function === popupFunctionName),
    [allData.functions, popupFunctionName],
  );

  const supportingDataMap = useMemo(() => ({
    middle_categories: { largeCategories: allData.large_categories || [] },
    small_categories: { middleCategories: allData.middle_categories || [] },
    oem_regions: {
      oems: allData.oems || [],
      regions: allData.regions || [],
    },
    schedule_templates: {
      categories: allData.categories || [],
      smallCategories: allData.small_categories || [],
      actors: allData.actors || [],
      departments: allData.departments || [],
    },
    permission_managements: {
      users: allData.users || [],
      functions: allData.functions || [],
      permissions: assignablePermissions,
      defaultFunction: popupFunctionName,
      defaultFunctionId: functionRecord?.id || null,
    },
  }), [allData, assignablePermissions, functionRecord, popupFunctionName]);

  const oems = allData.oems || [];
  const regions = allData.regions || [];
  const templateDef = getMasterDefinition('schedule_templates');
  const templateItems = allData.schedule_templates || [];

  const handleReload = useCallback(async (masterKey) => {
    try {
      const def = MASTER_DEFINITIONS.find((d) => d.key === masterKey)
        || USER_DEFINITIONS.find((d) => d.key === masterKey)
        || LIFE_DEFINITIONS.find((d) => d.key === masterKey);
      /* istanbul ignore next -- masterKey は既知のマスターキーのみ渡されるため def は必ず見つかる防御ガード */
      if (!def) return;
      const items = await getMasterItems(def.endpoint);
      setAllData((prev) => ({ ...prev, [masterKey]: items }));
    } catch (_err) {
      // individual reload failure — ignore silently
    }
  }, []);

  const handleOpenTemplatePopup = () => {
    /* istanbul ignore next -- ボタンは未選択時 disabled のため両IDは常に存在する防御ガード */
    if (!selectedOemId || !selectedRegionId) return;
    setGenerateResult(null);
    setTemplatePopupOpen(true);
  };

  const handleConfirmGenerate = async (templateIds, phaseMap = {}) => {
    setGenerating(true);
    setGenerateResult(null);
    try {
      const result = await generateFromTemplate({
        oem_id: Number(selectedOemId),
        region_id: Number(selectedRegionId),
        template_ids: templateIds,
        phase_map: phaseMap,
      });
      setGenerateResult(result);
      setTemplatePopupOpen(false);
    } catch (err) {
      setGenerateResult({ error: err.message || '登録に失敗しました。' });
    } finally {
      setGenerating(false);
    }
  };

  const handleNotionSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const result = await triggerNotionSync();
      setSyncResult(result);
      // 同期成功後にマスタデータをリロード
      loadAllData();
    } catch (err) {
      setSyncResult({ status: 'error', message: err.message || 'Notion同期に失敗しました。' });
    } finally {
      setSyncing(false);
    }
  };

  const handleFunctionChange = (e) => {
    setActiveFunction(e.target.value);
    setActiveKey(null);
    setPopupFunction(null);
  };

  const openPopup = (key, fnName) => {
    setActiveKey(key);
    /* istanbul ignore next -- 呼び出し元は必ず機能名を渡す */
    setPopupFunction(fnName || null);
  };

  const closePopup = () => {
    setActiveKey(null);
    setPopupFunction(null);
  };

  const activeDef = activeKey ? getMasterDefinition(activeKey) : null;

  /** 指定機能でフィルタした permission_managements を返す */
  const getItemsForKey = (key, fnName) => {
    if (key === 'permission_managements') {
      /* istanbul ignore next -- 呼び出し元は必ず機能名を渡す */
      const fn = fnName || activeFunction;
      return (allData.permission_managements || []).filter(
        (item) => item.function_name === fn,
      );
    }
    return allData[key] || [];
  };

  const isLife = activeFunction === 'Life';

  // 通常モード: ダッシュボード→全グループ、Life→Life基本マスタ+ユーザー権限、それ以外→ユーザー権限のみ
  const visibleGroups = isDashboard
    ? MASTER_GROUPS.filter((group) => isSuperAdmin || group.colorClass === 'user')
    : isLife
      ? [...LIFE_GROUPS, .../* istanbul ignore next -- 同上 */ (USER_GROUP ? [USER_GROUP] : [])]
      : /* istanbul ignore next -- 同上 */ (USER_GROUP ? [USER_GROUP] : []);

  /** カードグリッドを描画するヘルパー */
  const renderGroups = (groups, fnName) => (
    <div className="master-groups-grid">
      {groups.map((group) => (
        <div className={`master-group master-group--${group.colorClass}`} key={group.label}>
          <div className="master-group-header">
            <h3 className="master-group-title">{group.label}</h3>
          </div>
          <div className="master-group-cards">
            {group.keys.filter((key) => !SUPER_ADMIN_KEYS.has(key) || isSuperAdmin).map((key) => {
              const def = getMasterDefinition(key);
              /* istanbul ignore if -- グループのキーは全て定義済みマスタを指す */
              if (!def) return null;
              const count = getItemsForKey(key, fnName).length;
              return (
                <button
                  className="master-table-card"
                  key={key}
                  onClick={() => openPopup(key, fnName)}
                  type="button"
                >
                  <span className="master-table-card-label">{def.label}</span>
                  <span className="master-table-card-count">
                    {count}<span className="master-table-card-unit">件</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <DashboardPageFrame
      mainClassName="master-main"
      primaryTabActiveIndex={-1}
      primaryTabs={HEADER_TABS}
      rootClassName="master-page"
      sectionLabel=""
      stickyAreaClassName="master-sticky-area"
    >
      <section className="master-panel">
        {/* 機能選択ボックス */}
        <div className="master-select-area">
          <label className="master-select-label" htmlFor="master-function-select">機能</label>
          <select
            className="master-select-control"
            id="master-function-select"
            value={activeFunction}
            onChange={handleFunctionChange}
          >
            {getFunctionOptions().map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* 進捗スケジュール一括登録（ダッシュボード or システム管理 + superAdmin） */}
        {!loading && !error && (isDashboard || isSystemAdmin) && isSuperAdmin && (
          <div className="master-meta-card">
            <h3 className="master-meta-title">進捗スケジュール一括登録</h3>
            <p className="master-meta-description">
              OEM・対地を選択して、テンプレートから進捗スケジュールを一括登録します。
            </p>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginTop: '12px', flexWrap: 'wrap' }}>
              <label>
                OEM
                <select
                  value={selectedOemId}
                  onChange={(e) => setSelectedOemId(e.target.value)}
                  style={{ marginLeft: '8px' }}
                >
                  <option value="">選択してください</option>
                  {oems.map((o) => (
                    <option key={o.id} value={o.id}>{o.oem}</option>
                  ))}
                </select>
              </label>
              <label>
                対地
                <select
                  value={selectedRegionId}
                  onChange={(e) => setSelectedRegionId(e.target.value)}
                  style={{ marginLeft: '8px' }}
                >
                  <option value="">選択してください</option>
                  {regions.map((r) => (
                    <option key={r.id} value={r.id}>{r.region}</option>
                  ))}
                </select>
              </label>
              <button
                className="master-primary-button"
                disabled={!selectedOemId || !selectedRegionId || generating}
                onClick={handleOpenTemplatePopup}
                type="button"
              >
                {generating ? '登録中...' : '一括登録'}
              </button>
            </div>
            {generateResult?.error && (
              <div className="master-alert master-alert--error" style={{ marginTop: '12px' }}>
                {generateResult.error}
              </div>
            )}
            {generateResult && !generateResult.error && (
              <div className={`master-alert ${generateResult.skipped ? 'master-alert--error' : 'master-alert--success'}`} style={{ marginTop: '12px' }}>
                {generateResult.message}
              </div>
            )}
          </div>
        )}

        <TemplateSelectionPopup
          loading={generating}
          onClose={() => setTemplatePopupOpen(false)}
          onConfirm={handleConfirmGenerate}
          open={templatePopupOpen}
          phases={allData.phases || []}
          templates={templateItems.map((item) => templateDef.fromApi(item))}
        />

        {/* Notion同期（ローカル開発のみ / ダッシュボード or システム管理 + 管理者以上） */}
        {isNotionSyncEnabled() && !loading && !error && (isDashboard || isSystemAdmin) && (canSystemAdmin() || canAdmin('ダッシュボード')) && (
          <div className="master-meta-card">
            <h3 className="master-meta-title">Notion同期</h3>
            <p className="master-meta-description">
              Notionデータベースからデータを手動で同期します。（ローカル開発用。検証・商用では表示されません）
            </p>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginTop: '12px' }}>
              <button
                className="master-primary-button"
                disabled={syncing}
                onClick={handleNotionSync}
                type="button"
              >
                {syncing ? '同期中...' : 'Notion同期を実行'}
              </button>
            </div>
            {syncResult?.status === 'error' && (
              <div className="master-alert master-alert--error" style={{ marginTop: '12px' }}>
                {syncResult.message}
              </div>
            )}
            {syncResult && syncResult.status !== 'error' && (
              <div className={`master-alert ${syncResult.status === 'success' ? 'master-alert--success' : 'master-alert--error'}`} style={{ marginTop: '12px' }}>
                {syncResult.message || '同期が完了しました。'}
              </div>
            )}
          </div>
        )}

        {loading && <div className="master-empty-state">読み込み中...</div>}
        {!loading && error && <div className="master-alert master-alert--error">{error}</div>}

        {/* 通常モード: 選択機能のグループを表示 */}
        {!loading && !error && !isSystemAdmin && renderGroups(visibleGroups, activeFunction)}

        {/* システム管理モード: 各機能ごとにセクション分けて全グループ表示 */}
        {!loading && !error && isSystemAdmin && (
          <div className="master-system-sections">
            {SYSTEM_ADMIN_SECTIONS.map((section) => (
              <div className="master-function-section" key={section.function}>
                <h2 className="master-function-section-title">{section.label || section.function}</h2>
                {renderGroups(section.groups, section.function)}
              </div>
            ))}
          </div>
        )}

        {/* Table popup */}
        {activeDef && (
          <div className="ml-overlay" onClick={closePopup}>
            <div className="master-table-popup" onClick={(e) => e.stopPropagation()}>
              <div className="master-table-popup-header">
                <h3 className="master-table-popup-title">{activeDef.label}</h3>
                <button
                  className="master-table-popup-close"
                  onClick={closePopup}
                  type="button"
                >
                  ✕
                </button>
              </div>
              <div className="master-table-popup-body">
                <MasterCrudTable
                  definition={activeDef}
                  items={getItemsForKey(activeKey, popupFunction)}
                  onReload={() => handleReload(activeKey)}
                  readOnly={SUPER_ADMIN_KEYS.has(activeKey) && !isSuperAdmin}
                  supportingData={supportingDataMap[activeKey] || {}}
                />
              </div>
            </div>
          </div>
        )}
      </section>
    </DashboardPageFrame>
  );
}
